import { useCallback, useMemo, useState } from 'react';
import { createFileRoute, Link, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { BarChart3, Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { EntityIndexLayout, WorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  MEMBERSHIP_SORT_OPTIONS,
  MembershipListPanel,
} from '@/components/organisms/memberships/MembershipListPanel';
import { MembershipTableView } from '@/components/organisms/memberships/MembershipTableView';
import { MembershipDetailInSplitContext } from '@/components/organisms/memberships/membership-header-context';
import { RecoveryRenewalsWorkspace } from '@/components/organisms/recovery-renewals/RecoveryRenewalsWorkspace';
import {
  EMPTY_RECOVERY_RENEWALS_SEARCH,
  type RecoveryRenewalsSearch,
} from '@/components/organisms/recovery-renewals/recovery-renewals-model';
import { useHasPermission } from '@/hooks/usePermissions';
import {
  MEMBERSHIP_QUEUE_FILTERS,
  MEMBERSHIP_SOURCE_FILTERS,
  MEMBERSHIP_STATUS_FILTERS,
  type MembershipListFilters,
  type MembershipQueueFilter,
  type MembershipSourceFilter,
} from '@/lib/memberships/membership-list-model';
import type { MembershipStatus } from '@/types/membership';

type MembershipsView = 'members' | 'renewals';
// NOT-567 — the Renewals half of the old Recovery & Renewals workspace lives
// here as a tab. `view` is optional (members is the default) and distinct from
// the membership detail's own `tab` search param.
type MembershipsSearch = MembershipListFilters & { view?: 'renewals' };

export const Route = createFileRoute('/_dashboard/dashboard/memberships')({
  validateSearch: (search: Record<string, unknown>): MembershipsSearch => ({
    q: typeof search.q === 'string' ? search.q : '',
    status: MEMBERSHIP_STATUS_FILTERS.some((filter) => filter.value === search.status)
      ? (search.status as MembershipStatus | 'all')
      : 'all',
    source: MEMBERSHIP_SOURCE_FILTERS.some((filter) => filter.value === search.source)
      ? (search.source as MembershipSourceFilter)
      : 'all',
    queue: MEMBERSHIP_QUEUE_FILTERS.some((filter) => filter.value === search.queue)
      ? (search.queue as MembershipQueueFilter)
      : 'all',
    page: typeof search.page === 'number' && search.page > 0 ? Math.floor(search.page) : 1,
    sort:
      typeof search.sort === 'number' && search.sort >= 0
        ? Math.min(Math.floor(search.sort), MEMBERSHIP_SORT_OPTIONS.length - 1)
        : 0,
    view: search.view === 'renewals' ? 'renewals' : undefined,
  }),
  component: MembershipsLayout,
});

function MembershipsLayout() {
  const { location } = useRouterState();
  const router = useRouter();
  const isMobile = useIsMobile();
  const filters = Route.useSearch();
  const view: MembershipsView = filters.view === 'renewals' ? 'renewals' : 'members';

  // Renewals filters are ephemeral (queue triage), so they live in component
  // state rather than the URL — the members list owns the URL search shape.
  const [renewalsSearch, setRenewalsSearch] = useState<RecoveryRenewalsSearch>(
    EMPTY_RECOVERY_RENEWALS_SEARCH,
  );
  const handleRenewalsSearchChange = useCallback((patch: Partial<RecoveryRenewalsSearch>) => {
    setRenewalsSearch((previous) => ({ ...previous, ...patch, page: patch.page ?? 1 }));
  }, []);

  const idMatch = location.pathname.match(/\/dashboard\/memberships\/([^/]+)/);
  const isReportsRoute = location.pathname.includes('/dashboard/memberships/reports');
  const isNewRoute = location.pathname.includes('/dashboard/memberships/new');
  const isEditRoute = /\/dashboard\/memberships\/[^/]+\/edit/.test(location.pathname);
  const isFormMode = isNewRoute || isEditRoute;
  const selectedId = idMatch && !['reports', 'new'].includes(idMatch[1]) ? idMatch[1] : undefined;
  const hasChildRoute = Boolean(selectedId) || isReportsRoute || isNewRoute;
  const isIndexRoute = /^\/dashboard\/memberships\/?$/.test(location.pathname);
  // The renewals queue hits `/office/renewal-queue`, gated by `billing:view` (as
  // the old Recovery & Renewals nav item was). Users without it only see Members.
  const canViewRenewals = useHasPermission('billing:view');
  // The Members|Renewals toggle sits in the workspace header and stays visible
  // while a membership is open in the split detail (the detail suppresses its own
  // header there). It only steps aside for full-page routes and mobile detail.
  const showTabs =
    canViewRenewals && !isFormMode && !isReportsRoute && !(isMobile && Boolean(selectedId));
  const showRenewals = view === 'renewals' && canViewRenewals && !isFormMode && !isReportsRoute;

  const handleOpen = useCallback(
    (id: string) => {
      void router.navigate({
        to: '/dashboard/memberships/$id',
        params: { id },
        search: (previous) => previous as never,
      });
    },
    [router],
  );

  const handleNew = useCallback(() => {
    void router.navigate({
      to: '/dashboard/memberships/new',
      search: (previous) => previous as never,
    });
  }, [router]);

  const handleViewChange = useCallback(
    (next: MembershipsView) => {
      // Switching tabs returns to the index (drops any open membership detail) so
      // the renewals queue / members list takes the full pane.
      void router.navigate({
        to: '/dashboard/memberships',
        search: (previous) => {
          const nextSearch = { ...previous } as Record<string, unknown>;
          if (next === 'renewals') nextSearch.view = 'renewals';
          else delete nextSearch.view;
          return nextSearch as never;
        },
        replace: true,
      });
    },
    [router],
  );

  const handleFiltersChange = useCallback(
    (patch: Partial<MembershipListFilters>) => {
      void router.navigate({
        search: (previous) => {
          const next = { ...previous, ...patch } as Record<string, unknown>;

          if (!next.q) delete next.q;
          if (!next.status || next.status === 'all') delete next.status;
          if (!next.source || next.source === 'all') delete next.source;
          if (!next.queue || next.queue === 'all') delete next.queue;
          if (!next.page || next.page === 1) delete next.page;
          if (!next.sort || next.sort === 0) delete next.sort;

          return next as never;
        },
        replace: true,
      });
    },
    [router],
  );

  return (
    <WorkspaceShell hideHeader={isFormMode}>
      <MembershipsHeaderSlot
        hidden={isFormMode || (isMobile && hasChildRoute)}
        onNew={handleNew}
        showTabs={showTabs}
        view={view}
        onViewChange={handleViewChange}
      />
      {showRenewals ? (
        <>
          <Fullbleed />
          <RecoveryRenewalsWorkspace
            mode="renewals"
            search={renewalsSearch}
            onSearchChange={handleRenewalsSearchChange}
            title="Renewals"
            description="Upcoming and overdue membership renewals"
            bare
          />
        </>
      ) : (
        <EntityIndexLayout
          title="Memberships"
          storageKey="memberships-view"
          hasChildRoute={hasChildRoute}
          onResetToIndex={() =>
            void router.navigate({
              to: '/dashboard/memberships',
              search: (previous) => previous as never,
            })
          }
          splitWidthClass="w-[360px]"
          tableSheetWidthClass="w-[620px] sm:max-w-[620px]"
          viewToggleShowLabels
          viewToggleActiveTone="muted"
          hideListInSplit={isFormMode}
          fullPageFormInTable
          splitList={() => (
            <div className="flex h-full flex-col">
              <MembershipListPanel
                selectedId={selectedId}
                onSelect={handleOpen}
                autoSelectFirst={isIndexRoute}
                compact
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            </div>
          )}
          splitDetail={
            <MembershipDetailInSplitContext.Provider value={true}>
              <Outlet />
            </MembershipDetailInSplitContext.Provider>
          }
          tableView={({ openInSheet }) => (
            <MembershipTableView
              onOpen={(id) =>
                openInSheet(
                  () =>
                    void router.navigate({
                      to: '/dashboard/memberships/$id',
                      params: { id },
                      search: (previous) => previous as never,
                    }),
                )
              }
            />
          )}
          sheetContent={<Outlet />}
          mobileContent={
            <div className="flex h-full flex-col md:hidden">
              {isIndexRoute ? (
                <MembershipListPanel
                  selectedId={selectedId}
                  onSelect={handleOpen}
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                />
              ) : (
                <Outlet />
              )}
            </div>
          }
        />
      )}
    </WorkspaceShell>
  );
}

function MembershipsHeaderSlot({
  hidden,
  onNew,
  showTabs,
  view,
  onViewChange,
}: {
  hidden: boolean;
  onNew: () => void;
  showTabs: boolean;
  view: MembershipsView;
  onViewChange: (view: MembershipsView) => void;
}) {
  const canCreate = useHasPermission('memberships:create');
  const canViewReports = useHasPermission('memberships:view');

  const contextHeader = useMemo(() => {
    if (!showTabs) return null;
    return (
      <Tabs value={view} onValueChange={(value) => onViewChange(value as MembershipsView)}>
        <TabsList>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="renewals">Renewals</TabsTrigger>
        </TabsList>
      </Tabs>
    );
  }, [showTabs, view, onViewChange]);

  const newButton = useMemo(() => {
    if (hidden || !canCreate) return null;
    return (
      <Button size="sm" onClick={onNew}>
        <Plus size={14} className="mr-1.5" />
        New membership
      </Button>
    );
  }, [hidden, canCreate, onNew]);

  const toolbarActions = useMemo(() => {
    if (hidden || !canViewReports) return null;
    return (
      <Button variant="outline" size="sm" title="Reports" asChild>
        <Link to="/dashboard/memberships/reports" search={(prev) => prev as never}>
          <BarChart3 size={14} />
          Reports
        </Link>
      </Button>
    );
  }, [hidden, canViewReports]);

  useWorkspaceHeader({ contextHeader, newButton, toolbarActions }, [
    contextHeader,
    newButton,
    toolbarActions,
  ]);
  return null;
}
